import React, { useEffect, useState, useMemo } from 'react';
import { Table, message, Typography, Spin, Space, DatePicker, Button, Grid, Card, Radio, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { soDiBuongService, SoDiBuongReportDto } from '../services/soDiBuongService';
import dayjs, { Dayjs } from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

interface SoDiBuongReportProps {
    loai?: number;
}

interface ReportRow {
    key: string;
    criteriaName: string;
    total: string;
    [departmentName: string]: string;
}

const evalKeysDefault = ['thyl', 'capthuoc', 'congkhaithuoc', 'kettcp', 'ghitenthuoc', 'kimluu', 'xetiem', 'ghihs', 'huongdan', 'phoihopdn', 'giaotiep', 'rttq', 'ktchamsoc', 'xulydungcu', 'plrac', 'bb5s', 'thannhanbn', 'dongphucbn', 'ttb', 'changagoi', 'oxy', 'tulanh', 'suachuacsvc', 'tietkiem'];

const SoDiBuongReport: React.FC<SoDiBuongReportProps> = ({ loai = 1 }) => {
    const [reportData, setReportData] = useState<SoDiBuongReportDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [tableColumns, setTableColumns] = useState<ColumnsType<ReportRow>>([]);
    const [dataSource, setDataSource] = useState<ReportRow[]>([]);
    const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs());
    const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
    
    const [viewMode, setViewMode] = useState('table');
    const [selectedCriteriaKey, setSelectedCriteriaKey] = useState<string | null>(null);
    const [chartData, setChartData] = useState<any[]>([]);

    const screens = useBreakpoint();

    const evalKeys = useMemo(() => {
        if (loai === 2) {
            const hiddenFields = [
                'tongbn', 'sobnc1', 'sobnc2', 'sobnc3', 
                'ghitenthuoc', 'kimluu', 'ghihs', 
                'tuvanvv', 'tuvanrv', 
                'ktchamsoc', 'xulydungcu', 
                'thannhanbn', 'dongphucbn', 
                'changagoi', 'oxy'
            ];
            return evalKeysDefault.filter(k => !hiddenFields.includes(k));
        }
        return evalKeysDefault;
    }, [loai]);

    const renderEvalCell = (text: string) => {
        const parts = text.split(',').map(p => p.trim());
        return (
            <div style={{ textAlign: 'right' }}>
                {parts.map((part, index) => {
                    const [label, value] = part.split(':');
                    let color = 'black';
                    if (label.includes('Không kiểm tra')) color = 'orange';
                    if (label.includes('Chưa đạt')) color = 'red';
                    if (label.includes('Đạt')) color = 'green';
                    return (
                        <div key={index}>
                            <span style={{ color }}>{label}:</span>
                            <span style={{ fontWeight: 'bold' }}>{value}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const processReportData = (data: SoDiBuongReportDto[]) => {
        if (!data || data.length === 0) {
            setTableColumns([]);
            setDataSource([]);
            return;
        }

        const departmentNames = Array.from(new Set(data.flatMap(row => Object.keys(row.departmentValues))));

        const columns: ColumnsType<ReportRow> = [
            {
                title: 'Tiêu chí',
                dataIndex: 'criteriaName',
                key: 'criteriaName',
                fixed: 'left',
                width: 250,
                align: 'right',
            },
        ];

        departmentNames.forEach(deptName => {
            columns.push({
                title: deptName,
                dataIndex: deptName,
                key: deptName,
                align: 'right',
                render: (text: string, record: ReportRow) => evalKeys.includes(record.key) ? renderEvalCell(text) : text,
            });
        });

        columns.push({
            title: 'Tổng',
            dataIndex: 'total',
            key: 'total',
            fixed: 'right',
            align: 'right',
            width: 150,
            render: (text: string, record: ReportRow) => evalKeys.includes(record.key) ? renderEvalCell(text) : <span style={{ fontWeight: 'bold' }}>{text}</span>,
        });

        setTableColumns(columns);

        const newDataSource: ReportRow[] = data.map(item => {
            const row: ReportRow = {
                key: item.criteriaKey,
                criteriaName: item.criteriaName,
                total: item.total,
            };
            departmentNames.forEach(deptName => {
                row[deptName] = item.departmentValues[deptName] || '0';
            });
            return row;
        });
        setDataSource(newDataSource);

        // Set default criteria for chart
        if (data.length > 0 && !selectedCriteriaKey) {
            setSelectedCriteriaKey(data[0].criteriaKey);
        }
    };

    const fetchReport = async () => {
        if (!fromDate || !toDate) {
            message.error('Vui lòng chọn ngày bắt đầu và kết thúc cho báo cáo');
            return;
        }
        setLoading(true);
        try {
            const response = await soDiBuongService.getReport(fromDate.format('YYYY-MM-DD'), toDate.format('YYYY-MM-DD'), loai);
            setReportData(response.data); // Store raw data
            processReportData(response.data); // Process for table view
        } catch (error) {
            message.error('Lỗi khi tải báo cáo tổng hợp');
        } finally {
            setLoading(false);
        }
    };

    // Data transformation for chart
    useEffect(() => {
        if (!selectedCriteriaKey || reportData.length === 0) {
            setChartData([]);
            return;
        }

        const selectedData = reportData.find(item => item.criteriaKey === selectedCriteriaKey);
        if (!selectedData) return;

        const isEval = evalKeys.includes(selectedCriteriaKey);
        const transformedData = Object.entries(selectedData.departmentValues).map(([deptName, valueStr]) => {
            if (isEval) {
                const parts = valueStr.split(',').map(p => p.trim());
                const values = {
                    dat: 0,
                    chuaDat: 0,
                    khongKiemTra: 0,
                };
                parts.forEach(part => {
                    const [label, value] = part.split(':');
                    if (label.includes('Đạt')) values.dat = parseInt(value, 10) || 0;
                    else if (label.includes('Chưa đạt')) values.chuaDat = parseInt(value, 10) || 0;
                    else if (label.includes('Không kiểm tra')) values.khongKiemTra = parseInt(value, 10) || 0;
                });
                return { name: deptName, ...values };
            } else {
                return { name: deptName, value: parseInt(valueStr, 10) || 0 };
            }
        });

        setChartData(transformedData);
    }, [selectedCriteriaKey, reportData]);


    const renderChartView = () => {
        if (loading) return <Spin />;
        const isEval = evalKeys.includes(selectedCriteriaKey || '');
        
        // Custom Tooltip Component
        const CustomTooltip = ({ active, payload, label }: any) => {
            if (active && payload && payload.length) {
                return (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.96)',
                        padding: '12px 16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                        <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8, color: '#1890ff' }}>{label}</p>
                        {payload.map((entry: any, index: number) => (
                            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
                                <span style={{ fontWeight: 500 }}>{entry.name}:</span>{' '}
                                <span style={{ fontWeight: 'bold' }}>{entry.value}</span>
                            </p>
                        ))}
                        {isEval && (
                            <p style={{ margin: '8px 0 0 0', paddingTop: 8, borderTop: '1px solid #f0f0f0', fontWeight: 'bold' }}>
                                Tổng: {payload.reduce((sum: number, entry: any) => sum + entry.value, 0)}
                            </p>
                        )}
                    </div>
                );
            }
            return null;
        };

        // Custom formatter to hide zero values
        const labelFormatter = (value: any) => {
            return value > 0 ? value : '';
        };

        // Custom tick component for vertical labels on mobile
        const CustomXAxisTick = (props: any) => {
            const { x, y, payload } = props;
            
            if (screens.md) {
                // Desktop: horizontal labels
                return (
                    <g transform={`translate(${x},${y})`}>
                        <text
                            x={0}
                            y={0}
                            dy={16}
                            textAnchor="middle"
                            fill="#595959"
                            fontSize={12}
                        >
                            {payload.value}
                        </text>
                    </g>
                );
            } else {
                // Mobile: vertical labels
                return (
                    <g transform={`translate(${x},${y})`}>
                        <text
                            x={0}
                            y={0}
                            dy={5}
                            textAnchor="start"
                            fill="#595959"
                            fontSize={13}
                            fontWeight="500"
                            transform="rotate(90)"
                        >
                            {payload.value}
                        </text>
                    </g>
                );
            }
        };

        return (
            <div style={{ 
                backgroundColor: '#fafafa', 
                padding: screens.md ? '24px' : '16px', 
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}>
                <ResponsiveContainer width="100%" height={screens.md ? 450 : 500}>
                    <BarChart 
                        data={chartData} 
                        margin={{ top: 20, right: screens.md ? 30 : 10, left: screens.md ? 20 : 10, bottom: screens.md ? 20 : 140 }}
                    >
                        <defs>
                            {/* Gradient definitions for better visual appeal */}
                            <linearGradient id="colorDat" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#52c41a" stopOpacity={0.9}/>
                                <stop offset="95%" stopColor="#52c41a" stopOpacity={0.7}/>
                            </linearGradient>
                            <linearGradient id="colorChuaDat" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ff4d4f" stopOpacity={0.9}/>
                                <stop offset="95%" stopColor="#ff4d4f" stopOpacity={0.7}/>
                            </linearGradient>
                            <linearGradient id="colorKhongKiemTra" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#faad14" stopOpacity={0.9}/>
                                <stop offset="95%" stopColor="#faad14" stopOpacity={0.7}/>
                            </linearGradient>
                            <linearGradient id="colorDefault" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#1890ff" stopOpacity={0.9}/>
                                <stop offset="95%" stopColor="#1890ff" stopOpacity={0.7}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                            dataKey="name" 
                            tick={<CustomXAxisTick />}
                            tickLine={{ stroke: '#d9d9d9' }}
                            height={screens.md ? 30 : 140}
                            interval={0}
                        />
                        <YAxis 
                            allowDecimals={false}
                            tick={{ fill: '#595959', fontSize: screens.md ? 12 : 10 }}
                            tickLine={{ stroke: '#d9d9d9' }}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                        <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            iconType="rect"
                            iconSize={screens.md ? 14 : 12}
                        />
                        {isEval ? (
                            <>
                                <Bar 
                                    dataKey="dat" 
                                    stackId="a" 
                                    name="Đạt" 
                                    fill="url(#colorDat)"
                                    radius={[0, 0, 0, 0]}
                                    animationDuration={800}
                                >
                                    <LabelList dataKey="dat" position="center" fill="#fff" fontSize={screens.md ? 13 : 11} fontWeight="bold" formatter={labelFormatter} />
                                </Bar>
                                <Bar 
                                    dataKey="chuaDat" 
                                    stackId="a" 
                                    name="Chưa đạt" 
                                    fill="url(#colorChuaDat)"
                                    radius={[0, 0, 0, 0]}
                                    animationDuration={800}
                                >
                                    <LabelList dataKey="chuaDat" position="center" fill="#fff" fontSize={screens.md ? 13 : 11} fontWeight="bold" formatter={labelFormatter} />
                                </Bar>
                                <Bar 
                                    dataKey="khongKiemTra" 
                                    stackId="a" 
                                    name="Không kiểm tra" 
                                    fill="url(#colorKhongKiemTra)"
                                    radius={[4, 4, 0, 0]}
                                    animationDuration={800}
                                >
                                    <LabelList dataKey="khongKiemTra" position="center" fill="#fff" fontSize={screens.md ? 13 : 11} fontWeight="bold" formatter={labelFormatter} />
                                </Bar>
                            </>
                        ) : (
                            <Bar 
                                dataKey="value" 
                                name={reportData.find(d => d.criteriaKey === selectedCriteriaKey)?.criteriaName} 
                                fill="url(#colorDefault)"
                                radius={[8, 8, 0, 0]}
                                animationDuration={800}
                            >
                                <LabelList dataKey="value" position="top" fill="#595959" fontSize={screens.md ? 14 : 12} fontWeight="bold" formatter={labelFormatter} />
                            </Bar>
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderTableView = () => screens.md ? (
        <Table
            columns={tableColumns}
            dataSource={dataSource}
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
            sticky
            rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
    ) : (
        <div>
            {dataSource.map(item => (
                <Card key={item.key} title={item.criteriaName} style={{ marginBottom: 16 }}>
                    <p><Text strong>Tổng: {evalKeys.includes(item.key) ? <div style={{paddingLeft: 8}}>{renderEvalCell(item.total)}</div> : item.total}</Text></p>
                    <hr />
                    {Object.keys(item).filter(key => key !== 'key' && key !== 'criteriaName' && key !== 'total').map(deptName => (
                        <p key={deptName}><strong>{deptName}:</strong> {evalKeys.includes(item.key) ? <div style={{paddingLeft: 8}}>{renderEvalCell(item[deptName])}</div> : item[deptName]}</p>
                    ))}
                </Card>
            ))}
        </div>
    );

    return (
        <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
            <Title level={4}>Báo cáo tổng hợp</Title>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <DatePicker value={fromDate} onChange={setFromDate} />
                <DatePicker value={toDate} onChange={setToDate} />
                <Button type="primary" onClick={fetchReport}>Tìm kiếm báo cáo</Button>
            </Space>

            <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} style={{ marginBottom: 16 }}>
                <Radio.Button value="table">Bảng</Radio.Button>
                <Radio.Button value="chart">Biểu đồ</Radio.Button>
            </Radio.Group>
            
            {viewMode === 'chart' && (
                <div style={{ marginBottom: 16 }}>
                    <Text>Chọn chỉ số hiển thị: </Text>
                    <Select
                        style={{ width: 300 }}
                        value={selectedCriteriaKey}
                        onChange={(value) => setSelectedCriteriaKey(value)}
                        loading={loading}
                        placeholder="Chọn một chỉ số"
                    >
                        {reportData.map(item => (
                            <Option key={item.criteriaKey} value={item.criteriaKey}>{item.criteriaName}</Option>
                        ))}
                    </Select>
                </div>
            )}

            <Spin spinning={loading}>
                {viewMode === 'table' ? renderTableView() : renderChartView()}
            </Spin>
        </div>
    );
};

export default SoDiBuongReport;
